import { inject, injectable } from 'inversify';
import { action, computed, makeObservable } from 'mobx';
import { TYPES } from '@/engine/ioc';
import type { ViewEntity } from '@/engine/app/models/viewEntity/view.entity';
import type { ViewsService } from '@/engine/app/Views.service';
import type { ViewAvailabilityController } from '@/engine/app/modules/ui/UIConfigSchema';
import type { UIConfigResolver } from '@/engine/app/modules/ui';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { CommandProcessor } from '@/engine/app/modules/core/services/CommandProcessor';

@injectable()
export class NavigationButtonsController {
  private _viewsService: ViewsService;

  private _viewAvailabilityController: ViewAvailabilityController | undefined;

  private _commandProcessor: CommandProcessor;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.UIConfigResolver) uiConfigResolver: UIConfigResolver,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: CommandProcessor
  ) {
    makeObservable(this);

    this._viewAvailabilityController = uiConfigResolver.getOptionalValueByDescriptor('viewAvailabilityController');

    this._viewsService = viewsService;
    this._commandProcessor = commandProcessor;
  }

  public get viewIndex(): number {
    return this._viewsService.views.findIndex((view: ViewEntity) => {
      return view === this._viewsService.currentView!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
    });
  }

  @computed
  public get isLastView(): boolean {
    return this.viewIndex === this._viewsService.views.length - 1;
  }

  @computed
  public get isFirstView(): boolean {
    return this.viewIndex === 0;
  }

  @action
  public nextView(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
    this._commandProcessor.executeAnonymousCommand(
      (): Promise<undefined> => {
        if (!this.isLastView) {
          this._viewsService.setCurrentView(this._viewsService.views[this.viewIndex + 1]);
        }
        return Promise.resolve(undefined);
      },
      (): Promise<undefined> => {
        if (!this.isFirstView) {
          this._viewsService.setCurrentView(this._viewsService.views[this.viewIndex - 1]);
        }
        return Promise.resolve(undefined);
      }
    );
  }

  public isNextViewEnabled(): boolean {
    if (this.isLastView) {
      return false;
    }
    if (!this._viewAvailabilityController) {
      return true;
    }
    const nextView = this._viewsService.views[this.viewIndex + 1];
    const nextViewDescriptor = nextView.getDescriptor();

    return this._viewAvailabilityController.isViewEnabled(nextViewDescriptor);
  }

  @action
  public prevView(): void {
    this._commandProcessor
      .executeAnonymousCommand(
        (): Promise<undefined> => {
          if (!this.isFirstView) {
            this._viewsService.setCurrentView(this._viewsService.views[this.viewIndex - 1]);
          }
          return Promise.resolve(undefined);
        },
        (): Promise<undefined> => {
          if (!this.isLastView) {
            this._viewsService.setCurrentView(this._viewsService.views[this.viewIndex + 1]);
          }
          return Promise.resolve(undefined);
        }
      )
      .catch((error: Error) => {
        console.error(error);
      });
  }
}
